<template>
  <figure class="my-3 overflow-hidden rounded border border-gray-200 bg-white text-sm">
    <figcaption class="flex items-start justify-between gap-3 border-b border-gray-100 px-3 py-2">
      <div class="min-w-0">
        <p class="break-all text-xs font-bold text-primary">{{ asset.assetId }}</p>
        <p v-if="asset.description" class="mt-1 text-xs text-gray-700">{{ asset.description }}</p>
        <p class="mt-1 text-xs text-gray-400">
          {{ asset.type }}<span v-if="asset.sourcePage" class="ml-3">p.{{ asset.sourcePage }}</span>
        </p>
      </div>
      <button
        v-if="hasMarkdown && asset.pngPath"
        type="button"
        class="!m-0 flex-none !rounded !border !border-gray-300 !bg-white !px-2 !py-1 !text-xs !font-semibold !text-gray-600"
        @click="showSource = !showSource"
      >
        {{ showSource ? "Show formatted" : "View source image" }}
      </button>
    </figcaption>
    <div class="p-3">
      <div v-if="hasMarkdown && !showSource" class="asset-markdown overflow-x-auto" v-html="renderedMarkdown" />
      <p v-else-if="!asset.pngPath" class="text-xs text-yellow-700">No source image path is available.</p>
      <p v-else-if="loading" class="text-xs text-gray-500" role="status">Loading source image...</p>
      <p v-else-if="loadError" class="text-xs text-red-700" role="alert">{{ loadError }}</p>
      <a v-else-if="imageUrl" :href="imageUrl" target="_blank" rel="noopener" class="block">
        <img :src="imageUrl" :alt="asset.description || `Source image for ${asset.assetId}`" class="mx-auto max-h-screen max-w-full object-contain" />
      </a>
    </div>
  </figure>
</template>

<script setup>
import DOMPurify from "dompurify";
import { marked } from "marked";
import { computed, onBeforeUnmount, ref, watch } from "vue";

import { getRegAssetBlob } from "../api.js";

const props = defineProps({ asset: { type: Object, required: true } });
const showSource = ref(false);
const imageUrl = ref(null);
const loading = ref(false);
const loadError = ref("");
let loadSequence = 0;
const hasMarkdown = computed(() => Boolean(props.asset.markdown?.trim()));
const renderedMarkdown = computed(() => DOMPurify.sanitize(
  marked.parse(props.asset.markdown || "", { gfm: true, breaks: true })
));

function releaseImageUrl() {
  if (imageUrl.value) URL.revokeObjectURL(imageUrl.value);
  imageUrl.value = null;
}

async function loadImage(path) {
  const sequence = ++loadSequence;
  loading.value = true;
  try {
    const blob = await getRegAssetBlob(path);
    if (sequence === loadSequence) imageUrl.value = URL.createObjectURL(blob);
  } catch (error) {
    if (sequence === loadSequence) loadError.value = error?.message || "The source image could not be loaded.";
  } finally {
    if (sequence === loadSequence) loading.value = false;
  }
}

watch(
  () => [props.asset.pngPath, hasMarkdown.value, showSource.value],
  ([path, markdownAvailable, sourceVisible]) => {
    loadSequence++;
    releaseImageUrl();
    loading.value = false;
    loadError.value = "";
    if (path && (!markdownAvailable || sourceVisible)) loadImage(path);
  },
  { immediate: true }
);
watch(() => props.asset.assetId, () => { showSource.value = false; });
onBeforeUnmount(() => { loadSequence++; releaseImageUrl(); });
</script>

<style scoped>
.asset-markdown :deep(table) { width: 100%; border-collapse: collapse; }
.asset-markdown :deep(th), .asset-markdown :deep(td) { border: 1px solid #d1d5db; padding: 0.4rem 0.55rem; text-align: left; vertical-align: top; }
.asset-markdown :deep(th) { background: #f9fafb; font-weight: 700; }
.asset-markdown :deep(p + p) { margin-top: 0.65rem; }
</style>
